import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  untrack,
} from "solid-js";
import {
  SimulatedTransactionDetails,
  SimulatedTransactionResponse,
} from "~/server/types/brand";
import { Optional } from "~/types";
import { CtaState, DenominationPropsV2 } from "./useDenominationsGrid";
import { createStore, reconcile } from "solid-js/store";
import { toRupees } from "~/utils/number";

export type SimulateParams = {
  voucherProductId: string;
  amount: number;
  variantId: string;
  consumeCoins: boolean;
  denominationDetails?: { denomination: number; quantity: number }[];
};

export type SimulateFunction = (
  params: SimulateParams
) => Promise<SimulatedTransactionResponse>;

export type DenominationGridInternalStateV2 = ReturnType<
  typeof useDenominationGridStateV2
>;

export type DenominationCounts = { [denomination: number]: number };

export function useDenominationGridStateV2(props: {
  initialAmount: number;
  externalProps: DenominationPropsV2;
  onlySingleDenominationAllowed: boolean;
  maxVouchersPerOrder: number;
  isFixedDenominationBrand: boolean;
}) {
  const {
    externalProps,
    onlySingleDenominationAllowed,
    maxVouchersPerOrder,
    isFixedDenominationBrand,
  } = props;
  const { brand, simulateFunction } = externalProps;

  // Internal state signals
  const [selectedAmount, setSelectedAmount] = createSignal<number>(
    props.initialAmount
  );

  const [selectedVariantId, setSelectedVariantId] = createSignal<string | null>(
    externalProps.initialVariantId
  );
  const [useCoins, setUseCoins] = createSignal<boolean>(
    externalProps.initialConsumeCoins
  );

  // Initialize denomination counts from defaultDenominationDetails
  const initialDenominationCounts: DenominationCounts = {};
  for (const detail of brand.amountConditions.defaultDenominationDetails ??
    []) {
    initialDenominationCounts[detail.denomination] = detail.quantity;
  }

  const [denominationCounts, setDenominationCounts] =
    createStore<DenominationCounts>(initialDenominationCounts);

  // Get total vouchers count across all denominations
  const getTotalVouchersCount = createMemo(() => {
    return Object.values(denominationCounts).reduce(
      (sum, qty) => sum + (qty ?? 0),
      0
    );
  });

  // Update denomination count with single/multi mode logic
  function updateDenominationCount(denomination: number, newCount: number) {
    if (newCount < 0) return;

    if (onlySingleDenominationAllowed && newCount > 0) {
      // Single mode: Reset all other denominations to 0, set this one
      const clampedCount = Math.min(newCount, maxVouchersPerOrder);
      setDenominationCounts(reconcile({ [denomination]: clampedCount }));
    } else if (newCount === 0) {
      // Setting to 0, just update this denomination
      setDenominationCounts(denomination, 0);
    } else {
      // Multi-mode: check total doesn't exceed max
      const currentTotal = getTotalVouchersCount();
      const currentForThis = denominationCounts[denomination] ?? 0;
      const otherTotal = currentTotal - currentForThis;
      const allowedForThis = Math.max(0, maxVouchersPerOrder - otherTotal);
      const clampedCount = Math.min(newCount, allowedForThis);

      setDenominationCounts(denomination, clampedCount);
    }
  }

  // Effect to update selectedAmount whenever denominationCounts changes (for fixed denomination brands)
  createEffect(() => {
    if (!isFixedDenominationBrand) return;

    const totalAmount = Object.entries(denominationCounts).reduce(
      (sum, [denom, qty]) => sum + Number(denom) * (qty ?? 0),
      0
    );

    // if (totalAmount > 0) {
    setSelectedAmount(totalAmount);
    // }
  });

  const [simulationStore, setSimulationStore] = createStore<{
    response: Optional<SimulatedTransactionDetails>;
    error: Optional<string>;
    isSimulating: boolean;
  }>({
    response: null,
    error: null,
    isSimulating: false,
  });

  createEffect(() => {
    if (simulationStore.isSimulating) {
      setCtaState("LOADING");
    } else if (simulationStore.error) {
      setCtaState("DISABLED");
    } else {
      setCtaState("DEFAULT");
    }
  });

  const [_ctaState, _setCtaState] = createSignal<CtaState>("DEFAULT");

  const ctaState = externalProps.ctaState ?? _ctaState;
  const setCtaState = externalProps.setCtaState ?? _setCtaState;

  const getVouchersCount: Accessor<number> = createMemo(() => {
    if (isFixedDenominationBrand) {
      // doing this so that info is reflected quickly in ui (without waiting for simulate response)
      return getTotalVouchersCount();
    }
    return (
      simulationStore.response?.denominationDetails?.reduce(
        (total: number, item: any) => total + (item.quantity ?? 0),
        0
      ) ?? 0
    );
  });

  // Client-side validation for amount range
  const getAmountValidationError = (amount: number): string | null => {
    const { minAmount, maxAmount } = brand.amountConditions;

    if (amount < minAmount || amount > maxAmount) {
      return `Please select an amount between ${toRupees(minAmount)} and ${toRupees(maxAmount)}`;
    }
    return null;
  };

  // Request counter to track and invalidate stale responses
  let requestCounter = 0;

  createEffect(async () => {
    const newAmount = selectedAmount();
    const rewardCoins = useCoins();

    // Increment request counter for this request
    requestCounter++;
    const currentRequestId = requestCounter;

    // Check for client-side validation errors first
    const validationError = getAmountValidationError(newAmount);
    if (validationError) {
      setSimulationStore({
        response: null,
        error: validationError,
        isSimulating: false,
      });
      return;
    }

    await untrack(async () => {
      try {
        setSimulationStore({
          isSimulating: true,
        });

        const newSimulateResponse = await simulateFunction({
          voucherProductId: brand.externalId,
          amount: newAmount,
          variantId: selectedVariantId() ?? "",
          consumeCoins: rewardCoins === true,
          denominationDetails: getDenominationDetails(),
        });

        // Check if this response is still relevant (no newer request has been made)
        if (currentRequestId !== requestCounter) {
          return; // Stale response, ignore it
        }

        const upiTransactionDetails = newSimulateResponse.data.filter(
          (data: any) => data.paymentRoute != "WALLET"
        )[0];

        setSimulationStore({
          response: upiTransactionDetails,
          error: null,
          isSimulating: false,
        });
      } catch (e: any) {
        // Check if this error is still relevant
        if (currentRequestId !== requestCounter) {
          return; // Stale response, ignore it
        }
        setSimulationStore({
          error: e.message,
        });
      } finally {
        // Only update isSimulating if this is still the current request
        if (currentRequestId === requestCounter) {
          setSimulationStore({
            isSimulating: false,
          });
        }
      }
    });
  });

  const getCoinsDiscountAmount = () => {
    return simulationStore.response?.discountDetails?.coinDiscount.amount ?? 0;
  };

  const getDenominationDetails = () => {
    if (!isFixedDenominationBrand) return undefined;
    if (maxVouchersPerOrder === 1)
      return [{ denomination: selectedAmount(), quantity: 1 }];
    return Object.entries(denominationCounts)
      .filter(([_, qty]) => qty > 0)
      .map(([denom, qty]) => ({
        denomination: Number(denom),
        quantity: qty,
      }));
  };

  return {
    // State accessors
    selectedAmount,
    setSelectedAmount,
    selectedVariantId,
    setSelectedVariantId,
    useCoins,
    setUseCoins,
    ctaState,
    getVouchersCount,
    getCoinsDiscountAmount,
    simulationStore,
    // Denomination counts (for +/- selector)
    denominationCounts,
    updateDenominationCount,
    getTotalVouchersCount,
    getDenominationDetails,
    onlySingleDenominationAllowed,
    maxVouchersPerOrder,
  };
}
